import { PersistConfig, persistReducer } from 'redux-persist';
import { createReducer, getType } from 'typesafe-actions';

import { YoutubeActions } from '@lib/state/_actions';
import { produce } from 'immer';
import storage from '@lib/state/storage';

import { YoutubeVideo } from '@lib/domain/youtube';
import { OrderWithEmail, DefaultYoutubeProps } from '@lib/domain/order';
import { YoutubeState } from './types';

const stateKey = 'youtube';

/* ------------- Initial State ------------- */
const INITIAL_STATE: YoutubeState = {
    currentVideo: undefined,
    videos: undefined,
    channels: undefined,
    currentChannel: undefined,
    currentChannels: undefined,
    order: undefined,
    email: undefined,
    defaultValue: undefined,
    nextPageTokenVideo: undefined,
    videoSelected: [],
};

/* ------------- Reducers ------------- */
const setVideoSelected = (
    state: YoutubeState,
    { payload: { videos } }: ReturnType<typeof YoutubeActions.setVideoSelected>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.videoSelected = videos;
    });

const setYoutubeChannel = (
    state: YoutubeState,
    {
        payload: { channel },
    }: ReturnType<typeof YoutubeActions.setYoutubeChannel>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.currentChannels = channel;
        draft.channels = [];
        draft.videos = [];
        draft.videoSelected = [];
    });

const setYoutubeListChannels = (
    state: YoutubeState,
    {
        payload: { channel },
    }: ReturnType<typeof YoutubeActions.setYoutubeListChannels>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.channels = channel;
    });

const setYoutubeVideo = (
    state: YoutubeState,
    { payload: { video } }: ReturnType<typeof YoutubeActions.setYoutubeVideo>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.currentVideo = video;
    });

const setNextPageTokenVideo = (
    state: YoutubeState,
    {
        payload: { token },
    }: ReturnType<typeof YoutubeActions.setNextPageTokenVideo>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.nextPageTokenVideo = token;
    });

const setYoutubeListVideos = (
    state: YoutubeState,
    {
        payload: { videos },
    }: ReturnType<typeof YoutubeActions.setYoutubeListVideos>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.videos = videos;
    });

const setYoutubeOrder = (
    state: YoutubeState,
    { payload }: ReturnType<typeof YoutubeActions.setYoutubeOrder>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.order = payload.order;
        draft.defaultValue = payload.defaultValue;
    });

const setYoutubeDefault = (
    state: YoutubeState,
    { payload }: ReturnType<typeof YoutubeActions.setYoutubeDefault>,
): YoutubeState =>
    produce(state, (draft) => {
        draft.defaultValue = payload;
    });

/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(YoutubeActions.setYoutubeChannel)]: setYoutubeChannel,
    [getType(YoutubeActions.setYoutubeListChannels)]: setYoutubeListChannels,
    [getType(YoutubeActions.setYoutubeVideo)]: setYoutubeVideo,
    [getType(YoutubeActions.setYoutubeListVideos)]: setYoutubeListVideos,
    [getType(YoutubeActions.setYoutubeOrder)]: setYoutubeOrder,
    [getType(YoutubeActions.setYoutubeDefault)]: setYoutubeDefault,
    [getType(YoutubeActions.setNextPageTokenVideo)]: setNextPageTokenVideo,
    [getType(YoutubeActions.setVideoSelected)]: setVideoSelected,
});

const persistConfig: PersistConfig<YoutubeState> = {
    key: stateKey,
    storage,
};

const persistedReducer = persistReducer<
    YoutubeState,
    YoutubeActions.YoutubeActions
>(persistConfig, reducer);

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
// eslint-disable-next-line
const getReducerState = (state: any): YoutubeState => state[stateKey];

const selectors = {
    getCurrentChannel: (state: YoutubeState): YoutubeVideo | undefined =>
        state.currentChannels,
    getChannels: (state: YoutubeState): YoutubeVideo[] | undefined =>
        state.channels,
    getCurrentVideo: (state: YoutubeState): YoutubeVideo[] | undefined =>
        state.currentVideo,
    getVideos: (state: YoutubeState): YoutubeVideo[] | undefined =>
        state.videos,
    getOrder: (state: YoutubeState): OrderWithEmail | undefined => state.order,
    getDefaultValue: (state: YoutubeState): DefaultYoutubeProps | undefined =>
        state.defaultValue,
    getNextPageTokenVideo: (state: YoutubeState): string | undefined =>
        state.nextPageTokenVideo,
    getVideoSelected: (state: YoutubeState): string[] | undefined =>
        state.videoSelected,
};

/* ------------- Export ------------- */
const reducerObject = {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer: persistedReducer,
};

export default reducerObject;
